<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DSelectFieldSearch,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon
} from 'front-library'
import {
  generateReportList,
  getReportRequest
} from '@/app/core/services/reports'
import {
  getCalendars,
  Calendar,
  deleteCalendar,
  getYears
} from '@/app/core/services/calendars'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const itemsYears = ref([] as any[])
const searchYear = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()
const year = ref()
const tableCalendar = ref(null)
const calendarForDelete = ref()
const urlFile = ref(null)
const refresh = ref()
const payload = ref()

/* Datos componente table */
const columns = ref([
  { name: 'year', title: t('calendars.list.year'), table: 'year' },
  { name: 'period', title: t('calendars.list.period'), table: 'period' },
  {
    name: 'sub_period',
    title: t('calendars.list.subPeriod'),
    table: 'sub_period'
  },
  {
    name: 'start_date',
    title: t('calendars.list.initialDate'),
    table: 'start_date'
  },
  { name: 'end_date', title: t('calendars.list.finalDate'), table: 'end_date' },
  {
    name: 'updated_at',
    title: t('calendars.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<Calendar[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createCalendar = () => {
  void router.push({ name: 'CreateCalendar' })
}
const updateCalendar = (id: any) => {
  void router.push({ name: 'UpdateCalendar', params: { id } })
}
const getCalendar = (id: any) => {
  void router.push({ name: 'GetCalendar', params: { id } })
}

/* Eliminar calendario */
const removeCalendar = (values: any) => {
  titleModal.value = t('calendars.list.actionStatus')
  descriptionModal.value = t('modal.description')
  typeAlertModal.value = 'question'
  showModal.value = true
  calendarForDelete.value = values.id
}
const deleteConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showModal.value = false
  await deleteCalendar(calendarForDelete.value)
    .then(response => {
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('calendars.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      void calendars()
    })
    .catch(async (error: any) => {
      notificationError(error, 'calendars', t, notifications, loading)
    })
}

const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        String(item.name)
          .toLowerCase()
          .indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Generar reporte */
const downloadList = async () => {
  try {
    loading.value = true
    messageLoading.value = t('loading.report')
    urlFile.value = null
    const data = {
      code: 'configurations/entity-table',
      parameters: {
        query: JSON.stringify(payload.value),
        fields: payload.value.fields.filter(
          (field: any) => !field.includes('id')
        ),
        headers: columns.value
          .filter((item: any) => item.title !== '')
          .map((item: any) => item.title),
        module: t('calendars.list.title', 1)
      }
    }
    const id = await generateReportList(data)
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        descriptionModal.value = response.errors
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    notificationError(error, 'calendars', t, notifications, loading)
  }
}

/* Metodo para cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  urlFile.value = null
}

const calendars = async (isButton?: boolean) => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const data: any = await getCalendars(
      offset.value,
      perPage.value,
      sortBy.value,
      year.value,
      search.value === '' ? undefined : search.value
    )

    rows.value = data[0].results
    totalRows.value = data[0].totalItems
    payload.value = data[1]
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'calendars', t, notifications, loading)
  }
}

onMounted(async () => {
  itemsYears.value = await getYears()
})
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="
      t('modal.acceptQuestion', {
        action: t('table.action.delete')
      })
    "
    @closeModal="closeModal"
    @otherMethod="deleteConfirm()">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('reports.notification.viewFile')" />
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('calendars.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('calendars.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="grid grid-cols-12 md:grid-cols-12 items-end gap-3">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :label="t('calendars.list.search')"
            :icon="'SearchLgIcon'"
            class="col-span-6 sm:col-span-5 md:col-span-4"
            v-model="search"
            @keyup.enter="calendars" />
          <DSelectFieldSearch
            class="col-span-6 sm:col-span-5 md:col-span-4"
            :label="t('calendars.list.year')"
            v-model="year"
            :options="filterOptions(itemsYears, searchYear)"
            @filter="searchYear = $event" />
          <DBtn
            class="h-10 col-span-10 md:col-span-2 lg:col-span-2"
            @click.prevent="calendars(true)"
            v-html="t('form.button.search')" />
        </div>
        <div class="flex items-end gap-2 pt-3">
          <DBtn
            class="h-10 w-25"
            color="excel"
            type="button"
            @click="downloadList"
            v-html="t('form.button.download')" />
          <DBtn
            class="h-10 w-44"
            :icon="'PlusIcon'"
            type="button"
            @click="createCalendar()"
            v-if="store.hasPermission('calendar:create')">
            {{
              t('form.button.create', { form: t('calendars.list.title', 0) })
            }}
          </DBtn>
        </div>
      </div>
    </div>
    <DTable
      ref="tableCalendar"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), calendars()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), calendars()">
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('calendar:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getCalendar(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('calendar:update')"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateCalendar(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('calendar:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeCalendar(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
